/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { userSessionsRevoke } from "../funcs/userSessionsRevoke.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeUserSessionRequest,
  RevokeUserSessionSecurity,
} from "../models/operations/revokeusersession.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeUserSessionMutationVariables = {
  request: RevokeUserSessionRequest;
  security?: RevokeUserSessionSecurity | undefined;
  options?: RequestOptions;
};

export type RevokeUserSessionMutationData = void;

export type RevokeUserSessionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revokeUserSession userSessions
 *
 * @remarks
 * Push the session's jti into the revocation cache and soft-delete the row.
 */
export function useRevokeUserSessionMutation(
  options?: MutationHookOptions<
    RevokeUserSessionMutationData,
    RevokeUserSessionMutationError,
    RevokeUserSessionMutationVariables
  >,
): UseMutationResult<
  RevokeUserSessionMutationData,
  RevokeUserSessionMutationError,
  RevokeUserSessionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeUserSessionMutation(client, options),
    ...options,
  });
}

export function mutationKeyRevokeUserSession(): MutationKey {
  return ["@gram/client", "userSessions", "revoke"];
}

export function buildRevokeUserSessionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RevokeUserSessionMutationVariables,
  ) => Promise<RevokeUserSessionMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeUserSession(),
    mutationFn: function revokeUserSessionMutationFn({
      request,
      security,
      options,
    }): Promise<RevokeUserSessionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(userSessionsRevoke(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
